import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { unwrapLegacyUserText } from '../lib/chatWrapper.ts';
import { actionNote } from '../lib/actionNotes.ts';
import { SLIDE_TOOL_NAMES, DECK_TOOL_NAMES, isCardResolved } from '../lib/tools.ts';
import ToolCard from '../components/ToolCard.tsx';
import { Refresh } from '../lib/icons.tsx';
import type { ChatBlock, ChatMessage, StreamingBuffer, ToolCall, ToolUseBlock } from '../lib/chatTurns.ts';
import type { DeckApplyAllPayload, DeckApplyFailure } from '../../shared/api.ts';
import type { SlideEntry } from '../../shared/deck.ts';

// Human labels for the deck tools, for the Apply-all failure note.
const APPLY_TOOL_LABELS: Record<string, string> = {
  create_slide: 'new slide',
  edit_slide: 'slide edit',
  delete_slide: 'slide deletion',
  reorder_slides: 'slide reorder',
  reorder_sections: 'section reorder',
  create_section: 'new section',
  delete_section: 'section deletion',
};

/**
 * Turns the server's `failed` array into one plain sentence for the Apply-all
 * bar. A single failure names what broke and why (the raw server error, e.g.
 * "Slide file already exists…"); several just point at the cards below.
 */
function formatApplyAllError(failed: DeckApplyFailure[] | undefined) {
  const list = Array.isArray(failed) ? failed : [];
  const only = list.length === 1 ? list[0] : null;
  if (only) {
    const what = APPLY_TOOL_LABELS[only.tool] || 'change';
    return `Couldn't apply the ${what}: ${only.error || 'unknown error'}. Review the card below.`;
  }
  const n = list.length || 0;
  return `${n} change${n === 1 ? '' : 's'} could not be applied: review the card${n === 1 ? '' : 's'} below.`;
}

/**
 * A thread message as this transcript renders it. `card` is a slot for a
 * locally-built message to carry its own node; nothing feeds it today — the
 * new-deck intake uses `renderExtra` — so it is dormant, kept because the
 * renderer still reads it.
 */
export type TranscriptMessage = ChatMessage & { card?: ReactNode };

/** What the Apply-all bar is doing. Only the error arm carries a reason, and
 *  it carries EITHER the batch's per-card failures or a transport message —
 *  never both, since the second means the request never returned one. */
type ApplyAllState =
  | { kind: 'idle' }
  | { kind: 'applying' }
  | { kind: 'rejecting' }
  | { kind: 'error'; failed?: DeckApplyFailure[]; message?: string };

/** One card for the batch routes: the whole call for apply, the id alone for
 *  reject (the server needs nothing else to mark it). */
type ApplyAllCard = { tool: string; input?: ToolCall['input']; tool_id: string };

export type ConversationProps = {
  messages?: TranscriptMessage[] | null;
  /** The live buffer, or null between turns — this is the falling edge every
   *  effect here keys off, so it is null and not undefined when quiet. */
  streaming?: StreamingBuffer | null;
  slug: string;
  /** The CHAT SCOPE's slide: null in deck scope, which is also what turns the
   *  Apply-all bar on. */
  slide?: SlideEntry | null;
  slides?: SlideEntry[];
  onJumpToSlide?: (slideId: string) => void;
  onApplied?: (tool: ToolCall, result: unknown) => void;
  /** Deck scope only — Canvas passes null in slide scope, which is what the
   *  `pendingDeckCards` scan reads as "no batch bar here". */
  onApplyAll?: ((cards: ApplyAllCard[]) => Promise<DeckApplyAllPayload>) | null;
  onRejectAll?: ((cards: { tool_id: string }[]) => Promise<unknown>) | null;
  onAutoFix?: (message: string) => void;
  onSplitSlide?: (proposedJsx: string, targetSlide: SlideEntry) => void;
  onTighten?: (message: string) => void;
  onPendingChange?: (toolId: string, pending: boolean, keep: (() => void) | null) => void;
  /** Shown in place of an empty transcript. */
  empty?: ReactNode;
  /** The intake's own card slot, rendered after the last message. */
  renderExtra?: ReactNode;
  style?: CSSProperties;
};

/**
 * The conversation transcript above the command bar. Renders persisted
 * thread messages plus the live streaming turn; tool_use blocks render as
 * ToolCards (Preview / Apply / Reject). Used by both deck-editing chat and
 * the conversational new-deck intake (which feeds it locally built
 * messages and its own card slot via `renderExtra`).
 */
export default function Conversation({
  messages,
  streaming,
  slug,
  slide,
  slides,
  onJumpToSlide,
  onApplied,
  onApplyAll,
  onRejectAll,
  onAutoFix,
  onSplitSlide,
  onTighten,
  onPendingChange,
  empty,
  renderExtra,
  style,
}: ConversationProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Whole-deck "Apply all": the latest finished turn's still-pending deck-tool
  // cards, in thread (top-to-bottom) order. A turn that adds several slides at
  // once is fragile applied one card at a time — an inserted slide shifts the
  // anchor the next create points at — so we offer a single batch apply that
  // the server orders + remaps correctly (routes/chat.ts /_deck/apply-all).
  // Only in deck scope (slide == null) and once the stream has settled.
  const pendingDeckCards = useMemo<ToolUseBlock[]>(() => {
    if (!onApplyAll || slide || streaming != null) return [];
    const out: ToolUseBlock[] = [];
    const list = messages || [];
    for (let i = list.length - 1; i >= 0; i--) {
      const m = list[i];
      if (!m) continue;
      const blocks: ChatBlock[] = Array.isArray(m.content) ? m.content : [];
      if (m.role === 'user') {
        // Stop at the turn's user PROMPT; mid-turn user messages are tool_result only.
        if (typeof m.content === 'string' || blocks.some((b) => b.type === 'text')) break;
        continue;
      }
      if (m.role !== 'assistant') continue;
      for (const b of blocks) {
        if (b.type !== 'tool_use' || !DECK_TOOL_NAMES.has(b.name)) continue;
        if (isCardResolved(b)) continue;
        out.push(b);
      }
    }
    return out.reverse();
  }, [messages, streaming, slide, onApplyAll]);

  const [applyAllState, setApplyAllState] = useState<ApplyAllState>({ kind: 'idle' });
  // A fresh turn clears any stale failure note from the previous batch.
  useEffect(() => {
    if (streaming != null) setApplyAllState({ kind: 'idle' });
  }, [streaming]);

  // Superseded proposals: an overflow auto-fix round re-proposes the SAME slide,
  // stacking cards. Only the newest pending edit_slide per slide is actionable —
  // applying an older one writes stale JSX (only the newest is live on disk).
  // Collect the older ones (newest-first: first seen per slide wins) so the map
  // below can collapse them to a muted line. Persisted messages only.
  //
  // A RESOLVED card still claims its slide. It used to be skipped outright,
  // which meant deciding the newest proposal RESURRECTED the one it replaced:
  // an overflow rework left card A collapsed under card B, then Keep on B
  // marked B resolved, B left the scan, and A — still pending — became the
  // newest again, un-collapsing with live Apply/Discard on stale JSX and the
  // composer gated on it until you rejected it (Jake, 2026-08-04). So resolved
  // cards are still SEEN (they hold the slide); they just can't be superseded
  // themselves, which would collapse a legitimately applied card's history.
  const supersededIds = useMemo(() => {
    const superseded = new Set<string>();
    const seenSlide = new Set<string>();
    const list = messages || [];
    for (let i = list.length - 1; i >= 0; i--) {
      const m = list[i];
      if (!m || m.role !== 'assistant') continue;
      const blocks: ChatBlock[] = Array.isArray(m.content) ? m.content : [];
      for (const b of blocks) {
        if (b.type !== 'tool_use' || b.name !== 'edit_slide') continue;
        const key = b.input?.slide_id || '__scope__';
        if (!seenSlide.has(key)) seenSlide.add(key);
        else if (!isCardResolved(b)) superseded.add(b.id);
      }
    }
    return superseded;
  }, [messages]);

  async function runApplyAll() {
    if (applyAllState.kind === 'applying' || !onApplyAll) return;
    const cards = pendingDeckCards.map((b) => ({ tool: b.name, input: b.input, tool_id: b.id }));
    if (cards.length === 0) return;
    setApplyAllState({ kind: 'applying' });
    try {
      const res = await onApplyAll(cards);
      const failed = (res && res.failed) || [];
      setApplyAllState(failed.length ? { kind: 'error', failed } : { kind: 'idle' });
    } catch (err) {
      // Not the shared `errorMessage`: its fallback is the generic "Something
      // went wrong", and which of the two batch verbs failed is the whole
      // content of this sentence.
      setApplyAllState({
        kind: 'error',
        message: err instanceof Error && err.message ? err.message : 'Apply all failed',
      });
    }
  }

  async function runRejectAll() {
    if (applyAllState.kind === 'applying' || applyAllState.kind === 'rejecting' || !onRejectAll) return;
    const cards = pendingDeckCards.map((b) => ({ tool_id: b.id }));
    if (cards.length === 0) return;
    setApplyAllState({ kind: 'rejecting' });
    try {
      await onRejectAll(cards);
      setApplyAllState({ kind: 'idle' });
    } catch (err) {
      setApplyAllState({
        kind: 'error',
        message: err instanceof Error && err.message ? err.message : 'Reject all failed',
      });
    }
  }

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, streaming]);

  // Auto-preview: when a turn finishes (streaming → null) and its final
  // assistant message carries a single pending slide-edit proposal, preview
  // it immediately — the user should see the change, not a button. Tracked
  // on the persisted card (not the streaming one) because the streaming
  // message unmounts when the thread re-syncs, which would discard the
  // preview. The effect fires only on the streaming→null falling edge (the
  // prevStreaming ref guards re-runs from message-only updates), so the id is
  // set exactly once per finished stream.
  const [autoPreviewId, setAutoPreviewId] = useState<string | null>(null);
  const prevStreaming = useRef(streaming);
  useEffect(() => {
    if (prevStreaming.current != null && streaming == null) {
      // A finished turn spans several persisted messages (tool rounds plus a
      // closing text reply), so scan back to the turn's user PROMPT — the
      // mid-turn user messages hold only tool_result blocks — collecting
      // every still-pending proposal along the way.
      const pending: ToolUseBlock[] = [];
      const list = messages || [];
      for (let i = list.length - 1; i >= 0; i--) {
        const m = list[i];
        if (!m) continue;
        const blocks: ChatBlock[] = Array.isArray(m.content) ? m.content : [];
        if (m.role === 'user') {
          if (typeof m.content === 'string' || blocks.some((b) => b.type === 'text')) break;
          continue;
        }
        if (m.role !== 'assistant') continue;
        for (const b of blocks) {
          if (b.type !== 'tool_use') continue;
          if (!SLIDE_TOOL_NAMES.has(b.name) && !DECK_TOOL_NAMES.has(b.name)) continue; // reads
          if (isCardResolved(b)) continue;
          pending.push(b);
        }
      }
      setAutoPreviewId(pending.length === 1 ? (pending[0]?.id ?? null) : null);
    }
    prevStreaming.current = streaming;
  }, [streaming]); // eslint-disable-line react-hooks/exhaustive-deps

  const showApplyAll = pendingDeckCards.length >= 2;
  // A partial batch failure often drops the pending count below 2 (the applied
  // cards vanish, one failed create is left), which would unmount the whole bar
  // and hide WHY it failed. Keep the error visible independently of the button.
  const showApplyError = applyAllState.kind === 'error';

  return (
    <div className="conversation" ref={scrollRef} style={style}>
      {(!messages || messages.length === 0) && streaming == null && empty}
      {(messages || []).map((m, i) => (
        <Message
          key={i}
          role={m.role}
          content={m.content}
          card={m.card}
          slug={slug}
          slide={slide}
          slides={slides}
          onJumpToSlide={onJumpToSlide}
          onApplied={onApplied}
          onAutoFix={onAutoFix}
          onSplitSlide={onSplitSlide}
          onTighten={onTighten}
          onPendingChange={onPendingChange}
          supersededIds={supersededIds}
          autoPreviewId={autoPreviewId}
          turnStreaming={streaming != null}
        />
      ))}
      {streaming != null && (
        <Message
          role="assistant"
          content={streaming.text || ''}
          tools={streaming.tools}
          reasoning={streaming.reasoning}
          live
          slug={slug}
          slide={slide}
          slides={slides}
          onJumpToSlide={onJumpToSlide}
          onApplied={onApplied}
        />
      )}
      {(showApplyAll || showApplyError) && (
        <div className="conversation__apply-all">
          {showApplyAll && (
            <>
              <div className="conversation__apply-all-row">
                <span className="conversation__apply-all-label">{pendingDeckCards.length} deck changes proposed</span>
                <div className="conversation__apply-all-actions">
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={runRejectAll}
                    disabled={applyAllState.kind === 'applying' || applyAllState.kind === 'rejecting'}
                  >
                    {applyAllState.kind === 'rejecting' ? 'Rejecting…' : 'Reject all'}
                  </button>
                  <button
                    type="button"
                    className="btn btn--primary btn--sm"
                    onClick={runApplyAll}
                    disabled={applyAllState.kind === 'applying' || applyAllState.kind === 'rejecting'}
                  >
                    {applyAllState.kind === 'applying' ? 'Applying…' : `Apply all (${pendingDeckCards.length})`}
                  </button>
                </div>
              </div>
              <p className="conversation__apply-all-hint muted small">
                Applies them together in the right order, safer than one at a time when slides are added.
              </p>
            </>
          )}
          {showApplyError && (
            <p className="conversation__apply-all-error" role="alert">
              {applyAllState.message ? applyAllState.message : formatApplyAllError(applyAllState.failed)}
            </p>
          )}
        </div>
      )}
      {renderExtra}
    </div>
  );
}

type MessageProps = Pick<
  ConversationProps,
  | 'slug'
  | 'slide'
  | 'slides'
  | 'onJumpToSlide'
  | 'onApplied'
  | 'onAutoFix'
  | 'onSplitSlide'
  | 'onTighten'
  | 'onPendingChange'
> & {
  role: ChatMessage['role'];
  content?: ChatMessage['content'];
  /** The live turn's tool calls, which are not blocks yet — they arrive as
   *  bare `ToolCall`s off the stream and are concatenated with the persisted
   *  ones for rendering. */
  tools?: ToolCall[];
  card?: ReactNode;
  reasoning?: string;
  live?: boolean;
  supersededIds?: Set<string>;
  autoPreviewId?: string | null;
  turnStreaming?: boolean;
};

function Message({
  role,
  content,
  tools,
  card,
  reasoning,
  live,
  slug,
  slide,
  slides,
  onJumpToSlide,
  onApplied,
  onAutoFix,
  onSplitSlide,
  onTighten,
  onPendingChange,
  supersededIds,
  autoPreviewId,
  turnStreaming,
}: MessageProps) {
  const blocks: ChatBlock[] = Array.isArray(content) ? content : content ? [{ type: 'text', text: content }] : [];
  const rawText = blocks
    .filter((b) => b.type === 'text')
    .map((b) => b.text)
    .join('');
  // A studio-fired turn (a preview self-check correction, or a modal action like
  // Change template / Merge / Split) is a real user message the model reads, but
  // the human didn't type it. Render the one-line label instead of the whole
  // message: replacing the body IS how the fabricated prompt gets stripped from
  // the transcript. `title` keeps the instruction inspectable on hover.
  // A mid-turn `user` message carries only tool_result blocks — that is how the
  // wire format returns a tool's output, not something a person sent. Rendering
  // it produced an empty "You" bubble per tool round, which reads as the studio
  // having sent a message on your behalf and lost it (Jake, 2026-08-02). Two
  // other scans in this file already know the rule; the renderer didn't.
  const isToolResultOnly =
    role === 'user' && Array.isArray(content) && content.length > 0 && !content.some((b) => b.type === 'text');
  if (isToolResultOnly) return null;

  const studioNote = role === 'user' ? actionNote(rawText) : null;
  if (studioNote) {
    return (
      <div className="chat-msg chat-msg--note">
        <div className="chat-msg__note" role="status" title={rawText}>
          <Refresh className="chat-msg__note-icon" />
          <span>{studioNote}</span>
        </div>
      </div>
    );
  }
  const text = role === 'user' ? unwrapLegacyUserText(rawText) : rawText;
  // Widened to the common `ToolCall`: a persisted `tool_use` block carries a
  // `type` the live stream's calls don't, and the card renders both the same.
  const toolBlocks: ToolCall[] = [...blocks.filter((b) => b.type === 'tool_use'), ...(tools || [])];
  // While a run is in flight with no reply text yet, show the tail of its
  // stream as a liveness signal.
  const thinking = live && !text && reasoning;
  // …and say which of the two things it is actually doing. The label read
  // "Thinking" for the WHOLE run, so a turn that spent 50 of its 60 seconds in
  // the tool loop — reading the slide, editing, verifying — looked like a model
  // ruminating, and the obvious conclusion was that the effort dial was set too
  // high. The stream marks every tool call with ✎, so the first one is the
  // moment it stopped thinking and started working.
  const working = thinking && reasoning.includes('✎');
  return (
    <div className={'chat-msg chat-msg--' + role + (live ? ' chat-msg--live' : '')}>
      <div className="chat-msg__role">{role === 'user' ? 'You' : 'Studio'}</div>
      {thinking && (
        <div className="chat-msg__thinking" aria-live="polite">
          <span className="chat-msg__thinking-label">{working ? 'Working' : 'Thinking'}</span>
          <span className="chat-msg__thinking-tail">{reasoning.slice(-160)}</span>
        </div>
      )}
      {text && <div className="chat-msg__body">{text}</div>}
      {toolBlocks.map((t) => (
        <ToolCard
          key={t.id || t.name + JSON.stringify(t.input).slice(0, 40)}
          slug={slug}
          slide={slide}
          slides={slides}
          tool={t}
          live={live}
          autoPreview={!live && t.id != null && t.id === autoPreviewId}
          onJumpToSlide={onJumpToSlide}
          onApplied={onApplied}
          onAutoFix={onAutoFix}
          onSplitSlide={onSplitSlide}
          onTighten={onTighten}
          onPendingChange={onPendingChange}
          superseded={Boolean(supersededIds?.has(t.id))}
          turnStreaming={turnStreaming}
        />
      ))}
      {card}
    </div>
  );
}
